import { DateField } from "@/features/FilterTable/DateField";
import {
  dateSchema,
  formatDateValueToISOString,
  parseToDateValue,
} from "@/features/FormCard/utils";
import { postClientAction } from "@/lib/fetch";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCurrentPageFromUrl } from "@/hooks/useClientActions";
import { z } from "zod";

const GeneratePdfSchema = z.object({
  reportingDate: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional()
    .nullable()
    .describe("date"),
});

export type GeneratePdfSchemaType = z.infer<typeof GeneratePdfSchema>;

export default function GeneratePdf() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const { handleSubmit, setValue, watch, register, reset } =
    useForm<GeneratePdfSchemaType>({
      mode: "onChange",
      defaultValues: {
        reportingDate: undefined,
      },
      resolver: zodResolver(
        GeneratePdfSchema,
      ) as Resolver<GeneratePdfSchemaType>,
    });

  const currentPage = useCurrentPageFromUrl();

  const generatePdfMutation = useMutation({
    mutationFn: async (data: GeneratePdfSchemaType) => {
      const formattedReportingDate = formatDateValueToISOString(
        data.reportingDate,
      );

      return await postClientAction(
        `${import.meta.env.VITE_BACKEND_URL}/s1-quantilia/s1_download_center/generate`,
        {
          reportingDate: formattedReportingDate ?? "",
          prefix: "s1-instit",
          client: "s1-instit",
          action_button_name: t("reports.generate_action"),
          action_execution_page: currentPage ?? "",
        },
      );
    },
    onSuccess: () => {
      toast.success(t("reports.generate_started"));
      setIsOpen(false);
      reset();
    },
    onError: () => {
      toast.error(t("reports.generate_error"));
    },
  });

  const handleFormSubmit = (data: GeneratePdfSchemaType) => {
    generatePdfMutation.mutate(data);
  };

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          radius="full"
          style={{
            border: "solid 2px transparent",
            backgroundImage: `linear-gradient(hsl(var(--heroui-background)), hsl(var(--heroui-background))), linear-gradient(83.87deg, #F54180, #9353D3)`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
          endContent={<ChevronDownIcon className="h-4 w-4" />}
        >
          {t("reports.generate_action")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="space-y-4 p-1 w-full"
        >
          <div>
            <DateField
              name="reportingDate"
              label={t("templates.reporting_date")}
              watch={watch}
              setValue={setValue}
              register={register}
              required={true}
            />
          </div>

          <footer className="mt-4 w-full">
            <Button
              color="primary"
              radius="full"
              size="sm"
              type="submit"
              className="w-24"
              isLoading={generatePdfMutation.isPending}
            >
              {t("actions.generate")}
            </Button>
          </footer>
        </form>
      </PopoverContent>
    </Popover>
  );
}
